/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */

import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardFooter, Form } from 'reactstrap';
import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';
import { find, sortBy, compact } from 'lodash';
import { parse, subDays, endOfDay, subYears, format } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Field, FormAlerts, useFeatures } from '@cordata/buffet';
import { all } from 'axios';

import {
  usePatient,
  authorizedInstance as axios,
  referenceServices,
} from '~/utils';
import { deburrObject } from '~/utils/api/normalizers';

import LocationForm from '~/components/Location/MultiForm';

const PRIMARY_PHONES = [
  { label: 'Home', value: 'home' },
  { label: 'Mobile', value: 'mobile' },
  { label: 'Work', value: 'work' },
];

const serializeEmail = (e, p) => ({
  data: {
    ...e,
    type: 'email-addresses',
    email_address: e.emailAddress,
    links: {
      patient: {
        linkage: {
          type: 'patients',
          id: p.id,
        },
      },
    },
  },
});

const serializeAddress = (a, p) => ({
  data: {
    ...a,
    type: 'addresses',
    address1: a.address1,
    address2: a.address2,
    state: a.state && a.state.attributes.divisionAbbrevIso,
    address_type_uid: a.addressType && a.addressType.id,
    is_primary: a.isPrimary || false,
    is_in_use: true,
    post_code: a.postCode,
    links: {
      patient: {
        linkage: {
          type: 'patients',
          id: p.id,
        },
      },
    },
  },
});

const serializePhoneNumber = (a, p, primary) => ({
  data: {
    ...a,
    type: 'phone-number',
    number: a.number && a.number !== '' ? a.number.replace(/\D/g, '') : '',
    is_primary: a.label.toLowerCase() === primary,
    links: {
      patient: {
        linkage: {
          type: 'patients',
          id: p.id,
        },
      },
    },
  },
});

const fillPhones = currentList =>
  PRIMARY_PHONES.map(p =>
    (!currentList.map(c => c.label).includes(p.label)
      ? { label: p.label }
      : find(currentList, ['label', p.label])));

const initialValues = {};

const PatientsDemographicsForm = ({ data }) => {
  const [, , actions] = usePatient();
  const { hasAppCommunity } = useFeatures();
  const [isBirthDateRequired, setIsBirthDateRequired] = useState(!hasAppCommunity);
  const history = useHistory();
  const [races, setRaces] = useState([]);
  const [initialRaces, setinitialRaces] = useState([]);
  const [genders, setGenders] = useState([]);
  const [genderIdentities, setGenderIdentities] = useState([]);

  useEffect(() => {
    referenceServices.genderFetch().then(setGenders);
  }, []);

  useEffect(() => {
    referenceServices.genderIdentityFetch().then(setGenderIdentities);
  }, []);

  useEffect(() => {
    // because useFeatures doesn't return instantly
    setIsBirthDateRequired(!hasAppCommunity);
  }, [hasAppCommunity]);

  useEffect(() => {
    referenceServices.raceFetch().then(setRaces);
  }, []);

  useEffect(() => {
    axios
      .get(`/patients/${data.data.id}/races`)
      .then(r => r.data.data.map(deburrObject))
      .then(r => r.map((rr) => ({
        raceName: rr.raceName,
        raceLabel: rr.raceLabel,
        raceUid: rr.id,
      })))
      .then(setinitialRaces);
  }, []);

  return (
    <Formik
      displayName="PatientsDemographicsForm"
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize
      validationSchema={Yup.object().shape({
        firstName: Yup.string().required(),
        lastName: Yup.string().required(),
        birthDate: Yup.date()
          .min(format(subYears(new Date(), 100), 'MM/DD/YYYY'))
          .max(format(endOfDay(new Date()), 'MM/DD/YYYY'))
          .nullable()
          .when('isBirthDateRequired', {
            is: isBirthDateRequired,
            then: bd => bd.required(),
            otherwise: bd => bd.notRequired(),
          }),
        gender: Yup.mixed().required(),
        emails: Yup.array().of(Yup.object().shape({
            emailAddress: Yup.string().email(),
          })),
        primaryPhone: Yup.string().required(),
        phoneNumbers: Yup.array().of(Yup.object().shape({
            label: Yup.mixed(),
            number: Yup.mixed().when('label', (v, s) =>
              (localStorage.getItem('primary') === v.toLowerCase()
                ? s.required()
                : s.notRequired())),
          })),
      })}
      initialValues={{
        ...initialValues,
        ...data.data,
        birthDate: data.data.birthDate ? parse(data.data.birthDate) : '',
        race: initialRaces,
        ...[
          'phoneNumbers',
          'addresses',
          'emails',
          'gender',
          'genderIdentity',
        ].reduce((a, m) => {
          if (m === 'phoneNumbers') {
            const phoneNumbers = data.included.filter(i => i.type === 'phone-numbers');
            const primaryPhone = find(phoneNumbers, 'isPrimary');

            return {
              ...a,
              phoneNumbers: phoneNumbers.length
                ? sortBy(fillPhones(phoneNumbers), ['label'])
                : PRIMARY_PHONES.map(p => ({ label: p.value })),
              primaryPhone: primaryPhone
                ? primaryPhone.label.toLowerCase()
                : undefined,
            };
          } else if (m === 'addresses') {
            const addresses = data.included.filter(i => i.type === 'addresses');

            return {
              ...a,
              addresses,
            };
          } else if (m === 'emails') {
            const emails = data.included.filter(i => i.type === 'email-addresses');

            return {
              ...a,
              emails: emails.length ? emails : [{}],
            };
          } else if (m === 'gender') {
            const gender = find(
              genders,
              s => data.data.genderName === s.genderName,
            );

            return {
              ...a,
              gender,
            };
          } else if (m === 'genderIdentity') {
            const genderIdentity = find(
              genderIdentities,
              s => data.data.genderIdentityName === s.genderIdentityName,
            );

            return {
              ...a,
              genderIdentity,
            };
          }

          return a;
        }, {}),
      }}
      onSubmit={(values, { setErrors, setSubmitting }) => {
        const p = {
          data: {
            id: values.id,
            first_name: values.firstName,
            last_name: values.lastName,
            middle_name: values.middleName,
            birth_date: values.birthDate
              ? parse(values.birthDate).toISOString()
              : '',
            gender_uid: values.gender.genderUid,
            gender_identity_uid: values.genderIdentity?.genderIdentityUid,
            preferred_engagement: 'No Preference',
            links: {
              races: {
                linkage: values.race.map(r => ({
                  id: r.id || r.raceUid,
                  name: r.raceName,
                  type: 'reference-races',
                })),
              },
              gender: {
                linkage: {
                  id: values.gender.genderUid,
                },
                gender_identity: {
                  linkage: {
                    id: values.genderIdentity?.genderIdentityUid,
                  },
                },
              },
            },
          },
        };

        const addressPromises = values.addresses
          ? values.addresses.map(a =>
              (a.id
                ? axios.put(`/addresses/${a.id}`, serializeAddress(a, p.data))
                : axios.post('/addresses', serializeAddress(a, p.data))))
          : [];

        const emailPromises = compact(values.emails.map(e => e.emailAddress))
          .length
          ? values.emails.map(e =>
              (e.id
                ? axios.put(
                    `/email-addresses/${e.id}`,
                    serializeEmail(e, p.data),
                  )
                : axios.post('/email-addresses', serializeEmail(e, p.data))))
          : [];

        const phonePromises = values.phoneNumbers
          ? values.phoneNumbers.map(e =>
              (e.id
                ? axios.put(
                    `/phone-numbers/${e.id}`,
                    serializePhoneNumber(e, p.data, values.primaryPhone),
                  )
                : e.number &&
                  axios.post(
                    '/phone-numbers',
                    serializePhoneNumber(e, p.data, values.primaryPhone),
                  )))
          : [];

        all([
          axios.put(`/patients/${values.id}`, p),
          ...addressPromises,
          ...emailPromises,
          ...phonePromises,
        ])
          .then(() => actions.refresh())
          .then(() => history.push(`/patients/${values.id}`))
          .catch(e => setErrors({ server: e }))
          .then(() => setSubmitting(false));
      }}
    >
      {({
 handleSubmit, isSubmitting, submitCount, errors,
}) => (
  <Card>
    <Form onSubmit={handleSubmit} noValidate>
      {submitCount >= 1 && errors && <FormAlerts errors={errors} />}
      <fieldset>
        <Row form>
          <Col sm>
            <Field name="firstName" required />
          </Col>

          <Col sm={3}>
            <Field name="middleName" />
          </Col>

          <Col sm>
            <Field name="lastName" required />
          </Col>
        </Row>

        <Row form>
          <Col sm>
            <Field
              name="birthDate"
              type="date"
              required={isBirthDateRequired}
              maxDate={subDays(new Date(), 1)}
            />
          </Col>

          <Col sm>
            <Field
              label="Sex"
              name="gender"
              type="select"
              options={genders}
              getOptionValue={o => o.genderUid}
              getOptionLabel={o => o.genderName}
              required
            />
          </Col>

          <Col sm>
            <Field
              name="genderIdentity"
              label="Gender"
              type="select"
              options={genderIdentities}
              getOptionValue={o => o.genderIdentityUid}
              getOptionLabel={o => o.genderIdentityName}
            />
          </Col>

          <Col sm={6}>
            <Field
              label="Race"
              name="race"
              type="select"
              multi
              options={races}
              getOptionValue={o => o.raceUid}
              getOptionLabel={o => o.raceName}
            />
          </Col>
        </Row>
      </fieldset>

      <fieldset>
        <Row form>
          <Col sm>
            <FieldArray
              name="emails"
              render={({ form: { values: v } }) =>
                      v.emails.map(() => (
                        <Field
                          name={`emails.${0}.emailAddress`}
                          label="Email"
                          placeholder="Enter Email"
                          type="email"
                        />
                      ))
                    }
            />
          </Col>

          <Col sm>
            <Field
              name="primaryPhone"
              type="radio-group"
              options={PRIMARY_PHONES}
              required
              inline
              clearable={false}
            />
          </Col>

          <FieldArray
            name="phoneNumbers"
            render={({ form: { values: v } }) =>
                    v.phoneNumbers.map((o, i) => (
                      <Col sm={2} key={o.label}>
                        <Field
                          name={`phoneNumbers.${i}.number`}
                          label={`${v.phoneNumbers[i].label} Phone`}
                          placeholder={`Enter ${v.phoneNumbers[i].label} Phone`}
                          type="tel"
                          required={
                            v.primaryPhone ===
                            v.phoneNumbers[i].label.toLowerCase()
                          }
                        />
                      </Col>
                    ))
                  }
          />
        </Row>
      </fieldset>

      <fieldset>
        <FieldArray
          name="addresses"
          render={({
 push, remove, form, form: { values: v },
}) => (
  <>
    {!v.addresses.length && (
    <Button
      spear
      outline
      color="tertiary"
      size="sm"
      type="button"
      onClick={() => push({ postCode: '' })}
    >
      <FontAwesomeIcon icon="plus" />
      <span className="d-none d-md-inline ml-2">
        Add Address
      </span>
    </Button>
                    )}

    {v.addresses.map((a, i) => (
      <React.Fragment key={i}>
        <LocationForm {...form} index={i} />

        <div className="d-flex justify-content-center align-items-center py-2">
          {v.addresses.length > 0 && (
          <Button
            className="ml-auto mr-2"
            size="sm"
            outline
            color="danger"
            type="button"
            onClick={() => remove(i)}
          >
            <FontAwesomeIcon icon="minus" />
            <span className="d-none d-md-inline ml-2">
              Remove Address
            </span>
          </Button>
                          )}

          <Button
            className="mr-auto"
            outline
            color="tertiary"
            size="sm"
            type="button"
            onClick={() => push({ postCode: '' })}
          >
            <FontAwesomeIcon icon="plus" />
            <span className="d-none d-md-inline ml-2">
              Add Address
            </span>
          </Button>
        </div>

        {i !== v.addresses.length - 1 && <hr />}
      </React.Fragment>
                    ))}
  </>
                )}
        />
      </fieldset>

      <CardFooter className="d-flex">
        <div className="ml-auto">
          <Button
            type="cancel"
            disabled={isSubmitting}
            onClick={history.goBack}
          >
            Cancel
          </Button>

          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Working ...' : 'Save'}
          </Button>
        </div>
      </CardFooter>
    </Form>
  </Card>
      )}
    </Formik>
  );
};

export default PatientsDemographicsForm;
